/**
 * เครื่องมือ (tools of the eight trades, option A of docs/design/ideas/2026-10-11-item-categories.md, lane ITEMS).
 * A trade has one tool per job (a SLOT: axe, pick, rod, spade for the gatherers; hoe, sickle, watering can and seed bag for
 * the farmer; hammer, scissors, wok for the crafters), in three tiers: wood, iron, black iron. Tools are not bag items and
 * not stacks: they sit on a tool belt (`progress.trades.tools`, slot -> tool id), one per slot, never dropped, never worn
 * out (no durability in CBT1). Tier 1 is bought from the trade master (the merchant); tiers 2 and 3 are made by the
 * blacksmith, each from the one before.
 *
 * Without a tool the trade still works, but slower and with less (`NO_TOOL`): a tool is an upgrade, not a gate (only the
 * black-iron vein asks for an iron pick). Every number here is a placeholder for ns-balance (docs/team/inbox/balance.md).
 *
 * Data only; the rules are in `professions.ts` (`Trades`). No DOM, no timers.
 */
import type { ProfessionId, RecipeDef, WorkPose } from './professions';

export type ToolSlotId =
  | 'axe'
  | 'pick'
  | 'rod'
  | 'spade'
  | 'hoe'
  | 'sickle'
  | 'can'
  | 'seedbag'
  | 'hammer'
  | 'scissors'
  | 'wok';

export type ToolTier = 1 | 2 | 3;
export const TOOL_TIERS: readonly ToolTier[] = [1, 2, 3];

export interface ToolSlotDef {
  readonly id: ToolSlotId;
  readonly prof: ProfessionId;
  /** Thai: the generic name of the tool (the tiers add their material). */
  readonly name: string;
  /** The tier names, wood first. */
  readonly names: readonly [string, string, string];
  /** The pose it is held in at work (render/rig/tools.ts draws it; until a pose is drawn the trades show the closest drawn one). */
  readonly pose: WorkPose;
  /** What tier 1 costs at the trade master (ตำลึง; a placeholder for ns-balance). */
  readonly price: number;
}

const slot = (
  id: ToolSlotId,
  prof: ProfessionId,
  name: string,
  pose: WorkPose,
  price: number,
  names?: readonly [string, string, string],
): ToolSlotDef => ({ id, prof, name, pose, price, names: names ?? [`${name}ไม้`, `${name}เหล็ก`, `${name}เหล็กดำ`] });

export const TOOL_SLOT_LIST: readonly ToolSlotDef[] = [
  slot('axe', 'woodcutter', 'ขวาน', 'chop', 30),
  slot('pick', 'miner', 'อีเต้อ', 'pick', 40),
  slot('rod', 'fisher', 'คันเบ็ด', 'fish', 25),
  slot('spade', 'herbalist', 'เสียมขุดสมุนไพร', 'mend', 25),
  slot('hoe', 'farmer', 'จอบ', 'hoe', 30),
  slot('sickle', 'farmer', 'เคียว', 'sweep', 25),
  slot('can', 'farmer', 'บัวรดน้ำ', 'water', 35),
  slot('seedbag', 'farmer', 'ถุงเมล็ด', 'sow', 20, ['ถุงเมล็ดผ้า', 'ถุงเมล็ดหนัง', 'ถุงเมล็ดหนังดำ']),
  slot('hammer', 'blacksmith', 'ค้อนตีเหล็ก', 'hammer', 50),
  slot('scissors', 'tailor', 'กรรไกร', 'mend', 40, ['กรรไกรและเข็มไม้', 'กรรไกรและเข็มเหล็ก', 'กรรไกรและเข็มเหล็กดำ']),
  slot('wok', 'cook', 'กระทะ', 'sweep', 45, ['กระทะดิน', 'กระทะเหล็ก', 'กระทะเหล็กดำ']),
];
export const TOOL_SLOTS: Readonly<Record<ToolSlotId, ToolSlotDef>> = Object.fromEntries(
  TOOL_SLOT_LIST.map((s) => [s.id, s]),
) as Record<ToolSlotId, ToolSlotDef>;
export const isToolSlot = (v: unknown): v is ToolSlotId => typeof v === 'string' && v in TOOL_SLOTS;

export interface ToolDef {
  /** `<slot>-<tier name>`: axe-wood, axe-iron, axe-black. */
  readonly id: string;
  readonly slot: ToolSlotId;
  readonly tier: ToolTier;
  readonly name: string;
  readonly prof: ProfessionId;
}

const TIER_KEY = ['wood', 'iron', 'black'] as const;
export const toolId = (s: ToolSlotId, tier: ToolTier): string => `${s}-${TIER_KEY[tier - 1]}`;

export const TOOL_LIST: readonly ToolDef[] = TOOL_SLOT_LIST.flatMap((s) =>
  TOOL_TIERS.map((tier) => ({ id: toolId(s.id, tier), slot: s.id, tier, name: s.names[tier - 1]!, prof: s.prof })),
);
export const TOOLS: Readonly<Record<string, ToolDef>> = Object.fromEntries(TOOL_LIST.map((t) => [t.id, t]));
export const isToolId = (v: unknown): v is string => typeof v === 'string' && v in TOOLS;

/** What a tool of a tier does (every number a placeholder for ns-balance). */
export const TOOL_FX: Readonly<Record<ToolTier, { time: number; bonus: number; water: number; seedSave: number }>> = {
  // time: share of the base seconds (gather, harvest, plant, craft); bonus: extra chance of one more of each yield;
  // water: how many times the can's watering lasts; seedSave: chance a sown seed is not used up (the seed bag).
  1: { time: 0.9, bonus: 0.08, water: 1, seedSave: 0.1 },
  2: { time: 0.75, bonus: 0.16, water: 1.6, seedSave: 0.2 },
  3: { time: 0.6, bonus: 0.24, water: 2.2, seedSave: 0.3 },
};

/** With no tool in the slot: slower, and a gather gives less (never less than one). */
export const NO_TOOL = { time: 1.5, yieldMul: 0.6, bonus: 0, water: 0.5, waterTime: 1.6, seedSave: 0 } as const;

/** The tool slot a gathered node (by kind id) is worked with, and the lowest tier it needs (0: none; it works bare-handed). */
export const NODE_TOOL: Readonly<Record<string, { slot: ToolSlotId; tier: number }>> = {
  pine: { slot: 'axe', tier: 0 },
  oak: { slot: 'axe', tier: 0 },
  'iron-vein': { slot: 'pick', tier: 0 },
  // The black-iron vein breaks a wooden pick: it asks for an iron one.
  'dark-vein': { slot: 'pick', tier: 2 },
  'fish-spot': { slot: 'rod', tier: 0 },
  'herb-patch': { slot: 'spade', tier: 0 },
};

/** The farm's numbers: watering, manure, bait. */
export const FARM = {
  /** Seconds one watering (or manuring) takes, and how much slower by hand (no can). */
  waterTime: 2.5,
  /** Game seconds one watering lasts for a wood can; plots grow at full speed while wet, `dryRate` of it while dry (never die). */
  waterSeconds: 150,
  dryRate: 0.6,
  /** Manure: grows faster, one more of each yield on a chance, takes a moment to spread. */
  manureTime: 2,
  manureGrow: 1.25,
  manureBonus: 0.35,
  /** Bait (one used per cast): the rare catches come this many times as often. */
  baitRare: 2,
} as const;

/** What the blacksmith makes of the next tier: the same tool in iron, then black iron (needs the tier before in the belt). */
const upgrade = (s: ToolSlotDef, tier: 2 | 3): RecipeDef => ({
  id: `tool-${toolId(s.id, tier)}`,
  prof: 'blacksmith',
  level: tier === 2 ? 3 : 6,
  inputs: tier === 2
    ? [
        { id: 'iron-ingot', n: 3 },
        { id: 'hard-wood', n: 2 },
      ]
    : [
        { id: 'black-ingot', n: 3 },
        { id: 'hard-wood', n: 2 },
      ],
  out: { tool: toolId(s.id, tier) },
  time: tier === 2 ? 10 : 16,
  xp: tier === 2 ? 18 : 30,
});

/** The tier 2 and 3 recipes of every tool (added to the blacksmith's in data/professions.ts). */
export const TOOL_RECIPES: readonly RecipeDef[] = TOOL_SLOT_LIST.flatMap((s) => [upgrade(s, 2), upgrade(s, 3)]);

/** A tool of a tier in a slot (the belt's id), or undefined. */
export const toolOf = (belt: Readonly<Record<string, string>> | undefined, s: ToolSlotId): ToolDef | undefined => {
  const id = belt?.[s];
  return id ? TOOLS[id] : undefined;
};
